import { useState, FunctionComponent } from 'react'
import DOMPurify from 'dompurify'
import { Form, Button, Spinner, Alert } from 'react-bootstrap'
import { QuizResult } from 'Frontend/scripts/models/foodHabits.model'
import { useTranslation } from 'react-i18next'

type Props = {
  result: QuizResult
}

const ResultComponent: FunctionComponent<Props> = ({ result }) => {
  /* --- local state --- */
  const [email, setEmail]           = useState('')
  const [isSending, setIsSending]   = useState(false)
  const [status, setStatus]         = useState<{ success: boolean; message: string } | null>(null)
  /* --- helpers --- */
  const sendEmail = async () => {
    if (!email) return

    setIsSending(true)
    setStatus(null)

    try {
      const resultHTML = document.getElementById("foodhabits")?.outerHTML;

      await fetch('/api/foodhabit/sendresultmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          body: resultHTML,
          recipientEmail: email,
          resultEmailSubject: result.resultEmailSubject,
          heading: result.heading,
          categoryResults: result.categoryResults,
        }),
      })

      setStatus({ success: true, message: 'Resultatet har skickats!' })
      setEmail('')
    } catch (err: any) {
      setStatus({
        success: false,
        message: `Kunde inte skicka e-post: ${err.message}`,
      })
    } finally {
      setIsSending(false)
    }
  }

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    sendEmail()
  }

  /* --- sanitised HTML fragments --- */
  const { t } = useTranslation()
  // Sanitize HTML content
  const sanitizedPreamble = result.preamble ? DOMPurify.sanitize(result.preamble) : ''
  const sanitizedEndText  = result.endText  ? DOMPurify.sanitize(result.endText)  : ''

  return (
    <div className="result" id="result">
      <div className="result__header">
        <h1 className="result__header-heading">{result.heading}</h1>
        <p
          className="result__preamble text-wrap"
          dangerouslySetInnerHTML={{ __html: sanitizedPreamble }}
        />
      </div>

      {result.categoryResults?.map((cr, i) => (
        <div key={i} className="category">
          <div className="row">
            {cr.imageUrl && (
              <img
                id={`foodHabitImg-${i}`}
                src={cr.imageUrl}
                alt={cr.alternativeText}
                style={{ maxWidth: '100%' }}
              />
            )}
            <div className="category__body mt-3">
              <h2>{cr.heading}</h2>
              <p className="category__text text-wrap">{cr.text}</p>
              <p className="category__link">
                <i className="fa fa-angle-right" aria-hidden="true" />
                <a href={cr.linkUrl}>{cr.linkText}</a>
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* extra links */}
      <div className="category">
        <div className="row">
          <div className="category__body">
            <h2>{t('more-information')}</h2>
            {result.topNavigationLinks?.map((l, i) => (
              <p key={i} className="category__link category__link--no-border">
                <i className="fa fa-angle-right" aria-hidden="true" />
                <a href={l.url}>{l.caption}</a>
              </p>
            ))}
          </div>
        </div>
      </div>

      {/* closing text */}
      {result.endText && (
        <div
          className="result__endtext"
          dangerouslySetInnerHTML={{ __html: sanitizedEndText }}
        />
      )}

      {/* email form */}
      <div id="emailFoodHabits" className="result__email-form text-center mt-4">
        <h2>{t('enter-email-for-result')}</h2>

        <Form
          onSubmit={onSubmit}
          className="d-inline-flex flex-column align-items-center text-start"
          style={{ maxWidth: 400, width: '100%' }}
        >
          <Form.Group controlId="formEmail" className="mb-3">
            <Form.Label>
            {t('email')} ({t('required')})
            </Form.Label>
            <Form.Control
              type="email"
              aria-label={t('enter-email')}
              placeholder={t('enter-email')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-val-email={t('invalid-email')}
              data-val-required={t('field-required')}
              required
            />
          </Form.Group>

          <Button variant="primary" type="submit" disabled={!email || isSending}>
            {isSending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{' '}
                {t('sending')}...
              </>
            ) : (
              <>{t('send-result')}</>
            )}
          </Button>
        </Form>
        {status && (
          <Alert variant={status.success ? 'success' : 'danger'} className="mt-3">
            {status.message}
          </Alert>
        )}
      </div>
      </div>
  )
}

export default ResultComponent
